import { computed, onMounted, ref } from 'vue';
import { SUPPORTED_COUNTRIES } from '@/config/regional';

// Map country CODE → NAME for consumers that need display names
const CODE_TO_NAME = Object.fromEntries(
  SUPPORTED_COUNTRIES.map((c) => [c.code, c.name])
);

// Reverse: NAME → CODE (for writing back to canonical storage)
const NAME_TO_CODE = Object.fromEntries(
  SUPPORTED_COUNTRIES.map((c) => [c.name, c.code])
);

const CANONICAL_STORAGE_KEY = 'detected_country'; // Same key as userLocation.js
const DEFAULT_FALLBACK_COUNTRY = 'Australia';
const DEFAULT_FALLBACK_CODE = 'AU';

const canUseBrowserGlobals = () => typeof window !== 'undefined';

export function getLocaleCountryName({
  navigatorRef = typeof navigator !== 'undefined' ? navigator : null,
  intlApi = typeof Intl !== 'undefined' ? Intl : null,
} = {}) {
  if (!canUseBrowserGlobals() || !navigatorRef) return null;

  const locale = (navigatorRef.languages && navigatorRef.languages[0]) || navigatorRef.language;
  if (!locale || typeof locale !== 'string') return null;

  const parts = locale.split(/[-_]/);
  const region = parts.length > 1 ? parts[1].toUpperCase() : null;
  if (!region) return null;

  try {
    if (intlApi && typeof intlApi.DisplayNames === 'function') {
      const displayNames = new intlApi.DisplayNames([locale], { type: 'region' });
      return displayNames.of(region) || null;
    }
  } catch {
    // ignore locale parsing failures
  }

  return region;
}

export function getTimeZoneCountryName({
  intlApi = typeof Intl !== 'undefined' ? Intl : null,
} = {}) {
  if (!canUseBrowserGlobals() || !intlApi || typeof intlApi.DateTimeFormat !== 'function') return null;

  let timeZone = null;

  try {
    timeZone = intlApi.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = null;
  }

  if (!timeZone || typeof timeZone !== 'string') return null;

  if (timeZone.startsWith('Australia/')) return 'Australia';
  if (timeZone === 'Australia') return 'Australia';
  if (timeZone === 'Europe/London') return 'United Kingdom';
  if (timeZone.startsWith('Europe/')) return null;
  if (timeZone.startsWith('Pacific/Auckland')) return 'New Zealand';
  if (timeZone.startsWith('America/')) return null;
  if (timeZone.startsWith('Asia/')) return null;

  return null;
}

export function getDestinationCountryName(destination) {
  if (!destination) return null;

  if (typeof destination.country === 'string' && destination.country.trim()) {
    return destination.country.trim();
  }

  const components = destination.address_components;
  if (!Array.isArray(components)) return null;

  const countryComponent = components.find((component) => Array.isArray(component.types) && component.types.includes('country'));
  if (countryComponent?.long_name) return countryComponent.long_name;
  if (countryComponent?.short_name) return countryComponent.short_name;

  return null;
}

/**
 * Read the canonical detected country from localStorage (shared with userLocation.js).
 * Returns a country NAME (e.g. "Australia") for display purposes.
 * @param {Storage} storage - Storage backend (defaults to localStorage)
 */
function readCanonicalCountryName(storage) {
  try {
    const raw = storage.getItem(CANONICAL_STORAGE_KEY);
    if (!raw) return null;
    // If it's a code (e.g. "AU"), convert to name ("Australia")
    if (CODE_TO_NAME[raw]) return CODE_TO_NAME[raw];
    // If it's already a name (legacy data), return as-is
    return raw;
  } catch {
    return null;
  }
}

/**
 * Write the detected country to the canonical storage key.
 * Accepts either a country CODE ("AU") or NAME ("Australia").
 * @param {Storage} storage - Storage backend (defaults to localStorage)
 */
function writeCanonicalCountry(country, storage) {
  if (!country) return;
  try {
    // Normalize to CODE for canonical storage
    const code = NAME_TO_CODE[country] || (CODE_TO_NAME[country] ? country : null);
    if (code) {
      storage.setItem(CANONICAL_STORAGE_KEY, code);
    }
  } catch {
    // ignore storage write failures
  }
}

export function useDetectedCountry({
  selectedDestination,
  fallbackCountry = DEFAULT_FALLBACK_COUNTRY,
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  storage = canUseBrowserGlobals() ? window.localStorage : null,
  navigatorRef = typeof navigator !== 'undefined' ? navigator : null,
  intlApi = typeof Intl !== 'undefined' ? Intl : null,
} = {}) {
  const autoDetectedCountryName = ref(null);

  const getLocaleFallback = () => getLocaleCountryName({ navigatorRef, intlApi });
  const getTimeZoneFallback = () => getTimeZoneCountryName({ intlApi });

  const detectedCountryName = computed(() => {
    const destinationCountry = getDestinationCountryName(selectedDestination?.value);
    if (destinationCountry) return destinationCountry;

    if (selectedDestination?.value) {
      return autoDetectedCountryName.value;
    }

    return getTimeZoneFallback() || autoDetectedCountryName.value || getLocaleFallback();
  });

  const detectCountryNameOnLoad = async () => {
    // 1. Check canonical storage (written by userLocation.js detectLocationFromIP)
    if (storage) {
      const canonicalName = readCanonicalCountryName(storage);
      if (canonicalName) {
        autoDetectedCountryName.value = canonicalName;
        return canonicalName;
      }
    }

    const timeZoneCountry = getTimeZoneFallback();

    try {
      if (typeof fetchImpl !== 'function') {
        throw new Error('Fetch unavailable');
      }

      const response = await fetchImpl('https://ipapi.co/json/');
      if (!response.ok) throw new Error('IP API failed');

      const data = await response.json();
      const ipCountryName = typeof data.country_name === 'string' ? data.country_name.trim() : '';
      const finalName = timeZoneCountry || ipCountryName || getLocaleFallback() || null;

      autoDetectedCountryName.value = finalName;
      // Write to canonical storage so userLocation.js, currency, and trending all agree
      if (storage) writeCanonicalCountry(finalName, storage);

      return finalName;
    } catch {
      const fallback = timeZoneCountry || getLocaleFallback() || fallbackCountry;
      autoDetectedCountryName.value = fallback;
      if (storage) writeCanonicalCountry(fallback, storage);

      return fallback;
    }
  };

  onMounted(() => {
    void detectCountryNameOnLoad();
  });

  return {
    autoDetectedCountryName,
    detectedCountryName,
    detectCountryNameOnLoad,
  };
}
